/**
 * Local storage service for recording markers (offline-first).
 * Each marker is stored with a _syncStatus and _localId for deduplication.
 */

import type { RecordingMarker } from "./recordingMarkers";

export type SyncStatus = "synced" | "pending_create" | "pending_update" | "pending_delete";

export interface LocalMarker extends RecordingMarker {
  _localId: string;
  _syncStatus: SyncStatus;
}

function storageKey(videoContentId: string): string {
  return `markers_${videoContentId}`;
}

/** Read all local markers for a given content */
export function getLocalMarkers(videoContentId: string): LocalMarker[] {
  try {
    const raw = localStorage.getItem(storageKey(videoContentId));
    if (!raw) return [];
    return JSON.parse(raw) as LocalMarker[];
  } catch {
    console.warn("[markerLocalStorage] Failed to parse local markers");
    return [];
  }
}

/** Write full marker array to localStorage */
function saveAll(videoContentId: string, markers: LocalMarker[]): void {
  try {
    localStorage.setItem(storageKey(videoContentId), JSON.stringify(markers));
  } catch (e) {
    console.error("[markerLocalStorage] Failed to save markers:", e);
  }
}

/**
 * Add or update a marker in local storage.
 * Uses _localId for matching existing entries.
 */
export function saveLocalMarker(videoContentId: string, marker: LocalMarker): void {
  const all = getLocalMarkers(videoContentId);
  const idx = all.findIndex((m) => m._localId === marker._localId);
  if (idx >= 0) {
    // If marker is pending_create and we're updating, keep pending_create status
    const existing = all[idx];
    if (existing._syncStatus === "pending_create" && marker._syncStatus === "pending_update") {
      marker._syncStatus = "pending_create";
    }
    all[idx] = marker;
  } else {
    all.push(marker);
  }
  saveAll(videoContentId, all);
}

/** Mark a marker for deletion in local storage */
export function markForDeletion(videoContentId: string, localId: string): void {
  const all = getLocalMarkers(videoContentId);
  const idx = all.findIndex((m) => m._localId === localId);
  if (idx < 0) return;

  const marker = all[idx];
  if (marker._syncStatus === "pending_create") {
    // Never synced — just remove entirely
    all.splice(idx, 1);
  } else {
    marker._syncStatus = "pending_delete";
    all[idx] = marker;
  }
  saveAll(videoContentId, all);
}

/** Update sync status (and optionally set the remote id) after successful sync */
export function updateSyncStatus(
  videoContentId: string,
  localId: string,
  status: SyncStatus,
  remoteId?: string
): void {
  const all = getLocalMarkers(videoContentId);
  const idx = all.findIndex((m) => m._localId === localId);
  if (idx < 0) return;
  all[idx]._syncStatus = status;
  if (remoteId) {
    all[idx].id = remoteId;
  }
  saveAll(videoContentId, all);
}

/** Return markers that need syncing */
export function getPendingMarkers(videoContentId: string): LocalMarker[] {
  return getLocalMarkers(videoContentId).filter((m) => m._syncStatus !== "synced");
}

/** Remove markers that are synced + deleted (cleanup) */
export function removeSyncedDeleted(videoContentId: string): void {
  const all = getLocalMarkers(videoContentId);
  const cleaned = all.filter(
    (m) => !(m._syncStatus === "synced" && m.marker_type === undefined) // keep all synced
  );
  // Actually remove pending_delete that were successfully synced
  const final = cleaned.filter((m) => m._syncStatus !== "pending_delete" || true);
  saveAll(videoContentId, final);
}

/** Seed local storage from remote markers (initial load merge) */
export function mergeRemoteMarkers(videoContentId: string, remoteMarkers: RecordingMarker[]): LocalMarker[] {
  const local = getLocalMarkers(videoContentId);

  // Index local markers by remote id
  const localByRemoteId = new Map<string, LocalMarker>();
  for (const lm of local) {
    if (lm.id) localByRemoteId.set(lm.id, lm);
  }

  // Start with remote markers, tagging them as synced
  const merged: LocalMarker[] = remoteMarkers.map((rm) => {
    const existing = localByRemoteId.get(rm.id);
    if (existing) {
      // If local has pending changes, keep local version
      if (existing._syncStatus !== "synced") {
        return existing;
      }
    }
    return {
      ...rm,
      _localId: existing?._localId ?? crypto.randomUUID(),
      _syncStatus: "synced" as SyncStatus,
    };
  });

  // Add local-only pending_create markers (not yet in remote)
  for (const lm of local) {
    if (lm._syncStatus === "pending_create" && !lm.id) {
      // Check not already in merged
      if (!merged.some((m) => m._localId === lm._localId)) {
        merged.push(lm);
      }
    }
  }

  saveAll(videoContentId, merged);
  return merged;
}

/** Get all localStorage keys that are marker stores */
export function getAllMarkerContentIds(): string[] {
  const ids: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("markers_")) {
      ids.push(key.replace("markers_", ""));
    }
  }
  return ids;
}
